import React, { useMemo } from "react";
import { Box, Typography, useTheme, Stack, alpha, Theme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";
import { useInventoryMeasurementWithLocations } from "./InventoryMeasurementWithLocationsProvider";
import { NumberCell } from "src/components/common/dataGrid/NumberCell";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenant } from "hooks/settings";
import { useGetInventoryMeasurementComplianceQuery } from "src/store/api/generatedApi";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import {
  InventoryMeasurementSubmittedItem,
  DeliveryMeasurementSubmittedItem,
} from "../types";

interface ComplianceInfo {
  estimate: number;
  measured: number;
  diff: number;
}

interface CompliancePercentCellProps {
  value: number | null | undefined;
  theme: Theme;
  formatNumber: (value: number) => string;
}

const CompliancePercentCell: React.FC<CompliancePercentCellProps> = ({
  value,
  theme,
  formatNumber,
}) => {
  if (value === null || value === undefined) {
    return <NumberCell value={null} decimalPlaces={1} />;
  }

  const clampedForAlpha = Math.min(100, Math.max(0, value));
  const alphaValue = Math.pow(1 - clampedForAlpha / 100, 0.5);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        backgroundColor: alpha(theme.palette.data.blue, alphaValue),
        margin: "-16px",
        padding: "16px",
      }}
    >
      <Typography variant="body1Mono">{formatNumber(value)}%</Typography>
    </Box>
  );
};

export const InventoryMeasurementSubmittedSummary: React.FC = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { submittedItems, submittedDeliveryItems, submittedMeasurementSetId } =
    useInventoryMeasurementWithLocations();
  const theme = useTheme();
  const { format: formatNumber } = useNumberSerialiser({ decimalPlaces: 2 });

  const { data: complianceData } = useGetInventoryMeasurementComplianceQuery(
    {
      tenantName,
      measurementSetId: submittedMeasurementSetId ?? 0,
    },
    {
      skip: !submittedMeasurementSetId,
    }
  );

  const complianceMap = useMemo(() => {
    if (!complianceData?.items) return new Map<string, ComplianceInfo>();
    return new Map<string, ComplianceInfo>(
      complianceData.items.map((item) => [
        item.material_name,
        {
          estimate: item.quantity_scrap_bay_estimate,
          measured: item.quantity_scrap_bay,
          diff: item.diff_mass,
        },
      ])
    );
  }, [complianceData]);

  const columns: GridColDef<
    InventoryMeasurementSubmittedItem & { id: string }
  >[] = useMemo(
    () => [
      {
        field: "material_name",
        headerName: t("material"),
        flex: 1.5,
      },
      {
        field: "compliance",
        headerName: t("compliance"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem & { id: string }
          >
        ) => {
          if (params.row.id === "TOTAL") {
            const overallCompliance = (
              params.row as InventoryMeasurementSubmittedItem & {
                id: string;
                compliance: number | null;
              }
            ).compliance;
            return (
              <CompliancePercentCell
                value={overallCompliance}
                theme={theme}
                formatNumber={formatNumber}
              />
            );
          }

          const materialName = params.row.material_name;
          const complianceInfo = complianceMap.get(materialName);
          if (!complianceInfo || complianceInfo.measured === 0) {
            return <NumberCell value={null} decimalPlaces={1} />;
          }
          const discrepancyPct =
            (Math.abs(complianceInfo.diff) / complianceInfo.measured) * 100;
          const complianceScore = Math.max(0, 100 - discrepancyPct);

          return (
            <CompliancePercentCell
              value={complianceScore}
              theme={theme}
              formatNumber={formatNumber}
            />
          );
        },
      },
      {
        field: "quantity_scrap_bay",
        headerName: t("scrapBay"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem,
            number
          >
        ) => <NumberCell value={params.value} decimalPlaces={2} />,
      },
      {
        field: "quantity_yard",
        headerName: t("yard"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem,
            number
          >
        ) => <NumberCell value={params.value} decimalPlaces={2} />,
      },
      {
        field: "quantity_rail_car",
        headerName: t("railcar"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem,
            number
          >
        ) => <NumberCell value={params.value} decimalPlaces={2} />,
      },
      {
        field: "price",
        headerName: t("pricePerTonne"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem,
            number
          >
        ) => <NumberCell value={params.value} decimalPlaces={2} />,
      },
      {
        field: "total",
        headerName: t("total"),
        flex: 1,
        renderCell: (
          params: GridRenderCellParams<
            InventoryMeasurementSubmittedItem,
            number
          >
        ) => <NumberCell value={params.value} decimalPlaces={2} />,
      },
    ],
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- formatNumber is a locale-derived formatter with unstable identity; columns only need to rebuild when t, complianceMap, or theme change
    [t, complianceMap, theme]
  );

  const deliveryColumns: GridColDef<DeliveryMeasurementSubmittedItem>[] =
    useMemo(
      () => [
        {
          field: "material_name",
          headerName: t("material"),
          flex: 1.5,
        },
        {
          field: "quantity",
          headerName: t("quantity"),
          flex: 1,
          renderCell: (
            params: GridRenderCellParams<
              DeliveryMeasurementSubmittedItem,
              number
            >
          ) => <NumberCell value={params.value} decimalPlaces={2} />,
        },
      ],
      [t]
    );

  const rows = useMemo(() => {
    return (
      submittedItems?.map((item) => ({
        id: item.material_name,
        ...item,
      })) ?? []
    );
  }, [submittedItems]);

  const deliveryRows = useMemo(() => {
    return (
      submittedDeliveryItems?.map((item) => ({
        id: item.material_name,
        ...item,
      })) ?? []
    );
  }, [submittedDeliveryItems]);

  const totalRow = useMemo(() => {
    if (rows.length === 0) {
      return {
        id: "TOTAL",
        material_name: "All",
        compliance: null,
        quantity_scrap_bay: 0,
        quantity_yard: 0,
        quantity_rail_car: 0,
        price: 0,
        total: 0,
      };
    }

    const sums = rows.reduce(
      (acc, row) => ({
        quantity_scrap_bay: acc.quantity_scrap_bay + row.quantity_scrap_bay,
        quantity_yard: acc.quantity_yard + row.quantity_yard,
        quantity_rail_car: acc.quantity_rail_car + row.quantity_rail_car,
        total: acc.total + row.total,
        weightedPriceSum: acc.weightedPriceSum + row.price * row.total,
      }),
      {
        quantity_scrap_bay: 0,
        quantity_yard: 0,
        quantity_rail_car: 0,
        total: 0,
        weightedPriceSum: 0,
      }
    );

    const overallCompliance = complianceData?.compliance_score ?? null;

    return {
      id: "TOTAL",
      material_name: "All",
      compliance: overallCompliance,
      quantity_scrap_bay: sums.quantity_scrap_bay,
      quantity_yard: sums.quantity_yard,
      quantity_rail_car: sums.quantity_rail_car,
      price: sums.total ? sums.weightedPriceSum / sums.total : 0,
      total: sums.total,
    };
  }, [rows, complianceData]);

  const deliveryTotalRow = useMemo(() => {
    if (deliveryRows.length === 0) {
      return {
        id: "TOTAL",
        material_name: "All",
        quantity: 0,
      };
    }
    const total = deliveryRows.reduce((acc, row) => acc + row.quantity, 0);
    return {
      id: "TOTAL",
      material_name: "All",
      quantity: total,
    };
  }, [deliveryRows]);

  if (!submittedItems) {
    return <Typography>{t("noData")}</Typography>;
  }

  return (
    <Stack spacing={4} sx={{ width: "100%", mt: 2, alignItems: "center" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "80vw",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" gutterBottom align="center" sx={{ mb: 2 }}>
          {t("submittedInventorySummary")}
        </Typography>
        <Typography variant="h6" gutterBottom align="center" sx={{ mb: 2 }}>
          {t("inventoryWithColon")}
        </Typography>
        <HotkeyCompliantDataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          pinnedRows={{ bottom: [totalRow] }}
          getRowClassName={(params) =>
            params.id === "TOTAL" ? "total-row" : ""
          }
          sx={{
            "& .total-row": {
              fontStyle: "italic",
              ...theme.typography.body1Mono,
            },
            "& .total-row:hover": {
              backgroundColor: theme.palette.action.hover,
            },
            ".numeric": {
              display: "flex",
              justifyContent: "flex-end",
              ...theme.typography.body1Mono,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: "80vw",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" gutterBottom align="center" sx={{ mb: 2 }}>
          {t("submittedDeliverySummary")}
        </Typography>
        <HotkeyCompliantDataGrid
          rows={deliveryRows}
          columns={deliveryColumns}
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          pinnedRows={{ bottom: [deliveryTotalRow] }}
          getRowClassName={(params) =>
            params.id === "TOTAL" ? "total-row" : ""
          }
          sx={{
            "& .total-row": {
              fontStyle: "italic",
              ...theme.typography.body1Mono,
            },
            "& .total-row:hover": {
              backgroundColor: theme.palette.action.hover,
            },
            ".numeric": {
              display: "flex",
              justifyContent: "flex-end",
              ...theme.typography.body1Mono,
            },
          }}
        />
      </Box>
    </Stack>
  );
};
